import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '../../components/Layout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { TrendingDown, Play } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (v) => (v || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const monthEnd = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10);
};

const DepreciationRun = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('asset_master');
  const [form, setForm] = useState({
    company_code: '', period: new Date().toISOString().slice(0, 7),
    posting_date: monthEnd(), test_run: true,
  });
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/assets/depreciation-runs`, { withCredentials: true });
      setHistory(res.data);
    } catch (e) { /* non-blocking */ }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const run = async () => {
    setRunning(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/assets/depreciation-run`, form, { withCredentials: true });
      setResult(res.data);
      if (form.test_run) {
        toast.info(`Test run: ${res.data.assets_processed} asset(s), ${money(res.data.total_depreciation)}`);
      } else {
        toast.success(`Posted depreciation for ${res.data.assets_processed} asset(s)`);
        loadHistory();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Depreciation run failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="depreciation-run-page">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-orange-100 text-orange-700"><TrendingDown className="h-5 w-5" /></div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Depreciation Run</h1>
            <p className="text-gray-600 text-sm mt-0.5">Post periodic depreciation for a company code.</p>
          </div>
        </div>

        <Card className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            <div>
              <Label>Company Code</Label>
              <Input data-testid="dep-company-code" value={form.company_code} onChange={(e) => set('company_code', e.target.value)} placeholder="1000" />
            </div>
            <div>
              <Label>Period (month)</Label>
              <Input type="month" data-testid="dep-period" value={form.period} onChange={(e) => set('period', e.target.value)} />
            </div>
            <div>
              <Label>Posting Date</Label>
              <Input type="date" data-testid="dep-posting-date" value={form.posting_date} onChange={(e) => set('posting_date', e.target.value)} />
            </div>
            <div className="flex items-center gap-2 pb-2">
              <Checkbox id="test_run" data-testid="dep-test-run" checked={form.test_run} onCheckedChange={(v) => set('test_run', !!v)} />
              <Label htmlFor="test_run" className="cursor-pointer">Test run (no posting)</Label>
            </div>
          </div>
          <div className="mt-4">
            <Button onClick={run} disabled={!writable || running || !form.company_code || !form.period} data-testid="run-depreciation-btn">
              <Play className="h-4 w-4 mr-2" />{running ? 'Running...' : (form.test_run ? 'Simulate Run' : 'Post Depreciation')}
            </Button>
            {!writable && <span className="text-sm text-gray-400 ml-3">Read-only (no asset write permission)</span>}
          </div>
        </Card>

        {result && (
          <Card className="p-0 overflow-hidden" data-testid="dep-result">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">
                {result.test_run ? 'Simulation' : 'Posted'} — {result.period} ({result.company_code})
              </h3>
              <div className="text-sm text-gray-600">
                {result.assets_processed} asset(s) · Total <span className="font-semibold">{money(result.total_depreciation)}</span>
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Asset #</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Depreciation</TableHead>
                  <TableHead>Document</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {result.postings.length === 0 ? (
                  <TableRow><TableCell colSpan={4} className="text-center py-6 text-gray-500">No assets to depreciate for this period.</TableCell></TableRow>
                ) : result.postings.map((p) => (
                  <TableRow key={p.asset_number} data-testid={`dep-posting-${p.asset_number}`}>
                    <TableCell className="font-medium">{p.asset_number}</TableCell>
                    <TableCell>{p.description}</TableCell>
                    <TableCell className="text-right">{money(p.depreciation)}</TableCell>
                    <TableCell className="font-mono text-xs">{p.document || '(test)'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}

        <Card className="p-0 overflow-hidden">
          <div className="p-4 border-b"><h3 className="font-semibold text-gray-900">Run History</h3></div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Period</TableHead>
                <TableHead>Company</TableHead>
                <TableHead className="text-right">Assets</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead>Run By</TableHead>
                <TableHead>Run At</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {history.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-6 text-gray-500">No runs yet.</TableCell></TableRow>
              ) : history.map((h, i) => (
                <TableRow key={i}>
                  <TableCell className="font-medium">{h.period}</TableCell>
                  <TableCell>{h.company_code}</TableCell>
                  <TableCell className="text-right">{h.assets_processed}</TableCell>
                  <TableCell className="text-right">{money(h.total_depreciation)}</TableCell>
                  <TableCell>{h.run_by}</TableCell>
                  <TableCell className="text-xs text-gray-500">{h.run_at ? new Date(h.run_at).toLocaleString() : '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>
    </Layout>
  );
};

export default DepreciationRun;
